import React, { useEffect, useState } from 'react';

import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import AirOutlinedIcon from '@mui/icons-material/AirOutlined';
import { Box, Typography, useMediaQuery, Grid, Stack } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ContactEmergencyOutlinedIcon from '@mui/icons-material/ContactEmergencyOutlined';
import HearingOutlinedIcon from '@mui/icons-material/HearingOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { useSearchParams } from 'react-router-dom';

import PrimaryBlueButton from '../components/common/buttons/PrimaryBlueButton';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import useBackendApi from '../hooks/useBackendApi';

const backendURL = process.env.REACT_APP_BACKEND_URL;

const serviceItems = [
  {
    themeKey: 'hearingConservation',
    icon: HearingOutlinedIcon,
    title: 'Hearing Conservation',
    description:
      'Annual hearing testing and program support for employees enrolled in workplace hearing conservation programs.',
  },
  {
    themeKey: 'occupationalServices',
    icon: MedicalServicesOutlinedIcon,
    title: 'Occupational Services',
    description:
      'Employer-focused mobile health services that bring cost-efficient testing, screenings, and medical support to your site.',
  },
  {
    themeKey: 'respiratoryProtection',
    icon: AirOutlinedIcon,
    title: 'Respiratory Protection',
    description: 'On-site OSHA respiratory protection support with medical evaluations, training, and fit testing.',
  },
  {
    themeKey: 'emergencyPersonnelServices',
    icon: ContactEmergencyOutlinedIcon,
    title: 'Emergency Personnel Services',
    description:
      'Compliance-focused support for first responder teams across EMS, fire department, and police department programs.',
  },
  {
    themeKey: 'hospitalServices',
    icon: LocalHospitalOutlinedIcon,
    title: 'Hospital Services',
    description:
      'Respiratory protection and occupational health support built for hospitals and clinics with complex healthcare workplace needs.',
  },
  {
    themeKey: 'agriculturalServices',
    icon: AgricultureOutlinedIcon,
    title: 'Agricultural Services',
    description:
      'Mobile compliance services that help growers manage respiratory protection and field-ready workforce testing requirements.',
  },
  {
    themeKey: 'schoolVisionHearingScreening',
    icon: SchoolOutlinedIcon,
    title: 'School Vision & Hearing Screening',
    description:
      'Student screening services designed to support early identification of vision and hearing issues that impact learning.',
  },
];

const FeatureItem = ({
  icon: Icon,
  title,
  description,
  color,
  backgroundColor,
}: {
  icon: any;
  title: string;
  description: string;
  color: string;
  backgroundColor: string;
}) => (
  <Stack
    spacing={1}
    alignItems={{ xs: 'center', md: 'flex-start' }}
    sx={{
      textAlign: { xs: 'center', md: 'left' },
      padding: '16px',
      borderRadius: '12px',
      backgroundColor,
      height: '100%',
    }}
  >
    <Icon sx={{ color: color, fontSize: '32px' }} />
    <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: '600', color: color }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '14px', color: color, lineHeight: '1.4' }}>{description}</Typography>
  </Stack>
);

const FrostGuestHome = () => {
  const { theme } = useThemeContext();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<{ date: string; time: string } | null>(null);
  const [apptDetailsNotFound, setApptDetailsNotFound] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);

  const { apiFetchRequest } = useBackendApi();

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) {
      setToken(t);
      setLoading(true);
      fetchAppointmentDetails(t);
    } else {
      setToken(null);
      setLoading(false);
    }
  }, [searchParams]);

  const fetchAppointmentDetails = async (token: string) => {
    try {
      const response = await apiFetchRequest({
        endpoint: `${backendURL}/appointment/${token}`,
        method: 'GET',
      });

      if (response?.status !== 200) {
        console.warn(`Unexpected status: ${response?.status}`);
        setApptDetailsNotFound(true);
      } else if (response?.body?.date && response?.body?.timeslot) {
        setAppointmentDetails({
          date: response.body.date,
          time: response.body.timeslot,
        });
        setApptDetailsNotFound(false);
      } else {
        console.warn('Missing date or time in response body');
        setApptDetailsNotFound(true);
      }
    } catch (error) {
      console.error('Failed to fetch appointment details:', error);
      setApptDetailsNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteForms = async () => {
    setSubmitting(true);
    try {
      if (token) {
        window.location.href = `${backendURL}/forms/redirect?token=${token}`;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const textColor = theme.custom.pages.guest.home.typography.color;
  const guestHomeTheme = theme.custom.pages.guest.home;
  const guestHomeServicesTheme = guestHomeTheme.services;

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        display: 'flex',
        marginTop: '32px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: '32px',
        padding: { xs: '32px 24px', md: '48px' },
        borderRadius: '16px',
        boxShadow: theme.custom.report.details.resultTable.reportLabelTooltip.tooltip.boxShadowColor,
        [theme.breakpoints.down('md')]: {
          alignItems: 'center',
          flexDirection: 'column',
          textAlign: 'center',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'center', md: 'flex-start' },
          gap: '32px',
          alignSelf: 'stretch',
          flexDirection: { xs: 'column', md: 'row' },
          width: '100%',
        }}
      >
        {token && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            {!loading &&
              (apptDetailsNotFound ? (
                <Typography sx={{ fontSize: '16px', color: 'red', mb: 2 }}>
                  Appointment details not found or the link is invalid.
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: '16px', mb: 1, color: textColor }}>
                    Thank you for confirming your appointment on
                  </Typography>

                  <Typography sx={{ fontSize: '16px', mb: 2, fontWeight: 'bold', color: textColor }}>
                    {appointmentDetails ? `${appointmentDetails.date} at ${appointmentDetails.time}` : ''}
                  </Typography>

                  <Typography sx={{ fontSize: '16px', mb: 3, color: textColor }}>
                    Please click the button below to complete the mandatory forms
                  </Typography>

                  <PrimaryBlueButton
                    buttonText={'Complete forms'}
                    onClick={handleCompleteForms}
                    sxProps={{
                      width: isMobile ? '230px' : '250px',
                    }}
                  />
                </>
              ))}
          </Box>
        )}

        <Box
          component="img"
          src={'/images/frost/frost-thankyou.png'}
          alt="Guest Home"
          sx={{
            alignItems: 'center',
            width: { xs: '100%', sm: '300px', md: '360px' },
            height: { xs: 'auto', sm: '300px', md: '320px' },
            borderRadius: '16px',
            display: token ? { xs: 'none', md: 'block' } : 'block',
          }}
        />
        <Box
          sx={{
            flex: 2,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography
            sx={{
              color: textColor,
              typography: { sm: 'h5', md: 'h3' },
              alignSelf: 'stretch',
              fontSize: { xs: '28px', md: '40px' },
              fontStyle: 'normal',
              fontWeight: '700',
              lineHeight: { xs: '34px', md: '48px' },
              letterSpacing: '-0.25px',
              maxWidth: '100%',
              marginBottom: '40px',
            }}
          >
            The FROST Occupational Health & Compliance Portal
          </Typography>
          <Typography
            sx={{
              color: textColor,
              fontSize: '22px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '28px',
              letterSpacing: '0.25px',
              marginBottom: '16px',
            }}
          >
            A secure digital platform for on-site OSHA testing, health record management, and integrated scheduling.
          </Typography>
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
          <Grid item xs={12} md={4}>
            <FeatureItem
              icon={BarChartOutlinedIcon}
              title="Real-Time Compliance"
              description="Monitor your organization's OSHA compliance status via your personalized dashboard, ensuring you are audit-ready."
              color={textColor}
              backgroundColor={guestHomeTheme.featureCards.backgroundColor}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureItem
              icon={LockOutlinedIcon}
              title="Secure Data Access"
              description="Instantly download employee health records and certifications. We only ask for profile and email data to verify identity."
              color={textColor}
              backgroundColor={guestHomeTheme.featureCards.backgroundColor}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureItem
              icon={CalendarMonthOutlinedIcon}
              title="Smart Scheduling"
              description="Keep on-site testing organized with scheduling support, automatic updates, and timely reminders."
              color={textColor}
              backgroundColor={guestHomeTheme.featureCards.backgroundColor}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            backgroundColor: guestHomeTheme.disclosure.backgroundColor,
            padding: '20px',
            borderRadius: '12px',
            borderLeft: `4px solid ${guestHomeTheme.disclosure.borderColor}`,
          }}
        >
          <Typography sx={{ fontSize: '14px', color: textColor, lineHeight: '1.6' }}>
            <strong>Data Usage Disclosure:</strong> Fast Response On-Site Testing (FROST) requests access to
            'calendar.events' to facilitate automated scheduling. We do not access your personal calendar events or read
            emails. Your data is never shared.
          </Typography>
        </Box>

        <Box sx={{ width: '100%', marginTop: '36px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '12px', md: '24px' },
              marginBottom: '28px',
            }}
          >
            <Box sx={{ flex: 1, height: '1px', backgroundColor: guestHomeServicesTheme.dividerColor }} />
            <Typography
              sx={{
                color: textColor,
                fontSize: { xs: '28px', md: '40px' },
                fontWeight: '700',
                lineHeight: { xs: '34px', md: '48px' },
                whiteSpace: 'nowrap',
              }}
            >
              Our Services
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: guestHomeServicesTheme.dividerColor }} />
          </Box>

          <Grid container spacing={2}>
            {serviceItems.map(({ themeKey, icon: Icon, title, description }) => {
              const serviceColors = guestHomeServicesTheme.items[themeKey];

              return (
                <Grid item xs={12} sm={6} lg={4} key={title}>
                  <Stack
                    spacing={2}
                    sx={{
                      height: '100%',
                      padding: '20px',
                      borderRadius: '16px',
                      border: `1px solid ${guestHomeServicesTheme.card.borderColor}`,
                      backgroundColor: guestHomeServicesTheme.card.backgroundColor,
                      boxShadow: guestHomeServicesTheme.card.boxShadow,
                      textAlign: 'left',
                    }}
                  >
                    <Box
                      sx={{
                        width: '56px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '14px',
                        backgroundColor: serviceColors.surfaceColor,
                      }}
                    >
                      <Icon sx={{ color: serviceColors.accentColor, fontSize: '30px' }} />
                    </Box>

                    <Typography
                      sx={{
                        color: textColor,
                        fontSize: '18px',
                        fontWeight: '700',
                        lineHeight: '24px',
                        minHeight: { xs: 'auto', sm: '48px' },
                      }}
                    >
                      {title}
                    </Typography>

                    <Typography sx={{ color: textColor, fontSize: '15px', lineHeight: '24px', flexGrow: 1 }}>
                      {description}
                    </Typography>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default FrostGuestHome;
